import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BiChip } from '@euler/app/flows/bi-dashboards/components/ui/BiChip';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiDecimals,
  BiDisplayFormat,
  biDisplayFormatFitting,
  BiDisplayFormatKind,
  BiDisplayFormatOption,
  BiEmptyAs,
  formatBiValue,
  kBiDecimals,
  kBiDisplayFormatKindNames,
  kBiDisplayFormatOptions,
  kBiMaxFormatUnit,
} from '@euler/model/bi/format';
import { BiCell } from '@euler/model/bi/result';
import { memo } from 'react';
import { TextInput, View } from 'react-native';
import { FormatRow } from './FormatRow';
import { kBiFormatSamples } from './functions/biFormatSummary';

const kEmptyNames: Record<BiEmptyAs, string> = {
  [BiEmptyAs.Dash]: '显示 —',
  [BiEmptyAs.Zero]: '显示 0',
};

/**
 * 数字格式: a kind among those that fit the column, then only the options
 * that kind takes (decimals, 万 / 亿, unit, what an empty value shows), and
 * a sample of how a value reads.
 */
export const NumberFormatFields = memo(
  ({
    format,
    kinds,
    sample,
    onChange,
  }: {
    format: BiDisplayFormat;
    kinds: BiDisplayFormatKind[];
    /** A value of the column, shown as it would read; else the kind's. */
    sample?: BiCell;
    onChange: (format: BiDisplayFormat) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const options = kBiDisplayFormatOptions[format.kind];
    const set = (patch: Partial<BiDisplayFormat>) => {
      // An option set to undefined is removed: the format's default.
      const next = Object.fromEntries(
        Object.entries({ ...format, ...patch }).filter(
          ([, value]) => value !== undefined,
        ),
      ) as unknown as BiDisplayFormat;
      onChange(biDisplayFormatFitting(next));
    };
    return (
      <View style={{ gap: rpx(8) }}>
        {kinds.length > 1 ? (
          <FormatRow label="显示为">
            {kinds.map(kind => (
              <BiChip
                key={kind}
                label={kBiDisplayFormatKindNames[kind]}
                selected={format.kind === kind}
                onPress={() => set({ kind })}
              />
            ))}
          </FormatRow>
        ) : null}
        {options.includes(BiDisplayFormatOption.Decimals) ? (
          <FormatRow label="小数位">
            <BiChip
              label="自动"
              selected={format.decimals === undefined}
              onPress={() => set({ decimals: undefined })}
            />
            {kBiDecimals.map((decimals: BiDecimals) => (
              <BiChip
                key={decimals}
                label={`${decimals} 位`}
                selected={format.decimals === decimals}
                onPress={() => set({ decimals })}
              />
            ))}
          </FormatRow>
        ) : null}
        {options.includes(BiDisplayFormatOption.Compact) ? (
          <FormatRow label="大数">
            <BiChip
              label="完整"
              selected={!format.compact}
              onPress={() => set({ compact: undefined })}
            />
            <BiChip
              label="万 / 亿"
              selected={!!format.compact}
              onPress={() => set({ compact: true })}
            />
          </FormatRow>
        ) : null}
        {options.includes(BiDisplayFormatOption.Unit) ? (
          <FormatRow label="单位">
            <TextInput
              accessibilityLabel="单位"
              value={format.unit ?? ''}
              onChangeText={text =>
                set({ unit: text.trim() ? text : undefined })
              }
              maxLength={kBiMaxFormatUnit}
              placeholder="如 次、台、km，可不填"
              placeholderTextColor={aiColors.mutedForeground}
              style={{
                width: rpx(160),
                height: rpx(32),
                paddingHorizontal: rpx(10),
                borderRadius: rpx(8),
                borderWidth: 1,
                borderColor: aiColors.border,
                backgroundColor: aiColors.card,
                color: aiColors.foreground,
                fontSize: rpx(13),
              }}
            />
          </FormatRow>
        ) : null}
        {options.includes(BiDisplayFormatOption.EmptyAs) ? (
          <FormatRow label="无数据">
            {Object.values(BiEmptyAs).map(emptyAs => (
              <BiChip
                key={emptyAs}
                label={kEmptyNames[emptyAs]}
                selected={(format.emptyAs ?? BiEmptyAs.Dash) === emptyAs}
                onPress={() =>
                  set({
                    emptyAs: emptyAs === BiEmptyAs.Dash ? undefined : emptyAs,
                  })
                }
              />
            ))}
          </FormatRow>
        ) : null}
        <FormatRow label="示例">
          <Typography.Text medium size={rpx(13)} color={aiColors.foreground}>
            {formatBiValue(
              sample === undefined || sample === null
                ? kBiFormatSamples[format.kind]
                : sample,
              format,
            )}
          </Typography.Text>
        </FormatRow>
      </View>
    );
  },
);
